import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Plus, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const FiscalYearVariantForm = ({ editingItem, onSubmit, onCancel }) => {
  // Helper function for days in month
  const getDaysInMonth = (month) => {
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonth[month - 1];
  };

  // Initialize with default K4 template
  const getDefaultFormData = () => {
    const periods = [];
    // Generate 12 posting periods (Jan-Dec)
    for (let i = 1; i <= 12; i++) {
      periods.push({
        period_number: i,
        month_start: i,
        day_start: 1,
        month_end: i,
        day_end: getDaysInMonth(i),
        year_shift: 0
      });
    }
    // Add 4 special periods
    for (let i = 13; i <= 16; i++) {
      periods.push({
        period_number: i,
        month_start: 12,
        day_start: 31,
        month_end: 12,
        day_end: 31,
        year_shift: 0
      });
    }

    return {
      fiscal_year_variant_id: 'K4',
      description: 'Calendar Year, Jan–Dec, 4 Special Periods',
      calendar_year_flag: true,
      year_dependent_flag: false,
      number_of_posting_periods: 12,
      number_of_special_periods: 4,
      periods: periods
    };
  };

  const getV3Template = () => {
    const periods = [];
    // Apr-Dec (periods 1-9)
    for (let i = 1; i <= 9; i++) {
      periods.push({
        period_number: i,
        month_start: i + 3,
        day_start: 1,
        month_end: i + 3,
        day_end: getDaysInMonth(i + 3),
        year_shift: 0
      });
    }
    // Jan-Mar (periods 10-12) with year_shift = 1
    for (let i = 10; i <= 12; i++) {
      const month = i - 9; // 1, 2, 3
      periods.push({
        period_number: i,
        month_start: month,
        day_start: 1,
        month_end: month,
        day_end: getDaysInMonth(month),
        year_shift: 1
      });
    }
    // 2 special periods on Mar 31
    for (let i = 13; i <= 14; i++) {
      periods.push({
        period_number: i,
        month_start: 3,
        day_start: 31,
        month_end: 3,
        day_end: 31,
        year_shift: 1
      });
    }

    return {
      fiscal_year_variant_id: 'V3',
      description: 'Apr–Mar Fiscal Year (India)',
      calendar_year_flag: false,
      year_dependent_flag: false,
      number_of_posting_periods: 12,
      number_of_special_periods: 2,
      periods: periods
    };
  };

  const [formData, setFormData] = useState(() => 
    editingItem || getDefaultFormData()
  );

  // Simple effect that only runs when editingItem changes
  useEffect(() => {
    if (editingItem) {
      setFormData(editingItem);
    }
  // eslint-disable-next-line
  }, [editingItem?.fiscal_year_variant_id]);

  // Template application
  const applyTemplate = (templateName) => {
    if (templateName === 'K4') {
      setFormData(getDefaultFormData());
    } else if (templateName === 'V3') {
      setFormData(getV3Template());
    }
  };

  // Period management functions
  const generatePeriods = () => {
    const totalPeriods = parseInt(formData.number_of_posting_periods) + 
                        parseInt(formData.number_of_special_periods);
    
    const newPeriods = Array.from({ length: totalPeriods }, (_, i) => ({
      period_number: i + 1,
      month_start: i < 12 ? i + 1 : 12,
      day_start: 1,
      month_end: i < 12 ? i + 1 : 12,
      day_end: i < 12 ? getDaysInMonth(i + 1) : 31,
      year_shift: 0
    }));
    
    setFormData(prev => ({ ...prev, periods: newPeriods }));
  };

  const addPeriod = () => {
    const newPeriod = {
      period_number: formData.periods.length + 1,
      month_start: 1,
      day_start: 1,
      month_end: 1,
      day_end: 31,
      year_shift: 0
    };
    setFormData(prev => ({
      ...prev,
      periods: [...prev.periods, newPeriod]
    }));
  };

  const removePeriod = (index) => {
    const updatedPeriods = formData.periods.filter((_, i) => i !== index);
    // Renumber periods
    updatedPeriods.forEach((period, i) => {
      period.period_number = i + 1;
    });
    setFormData(prev => ({ ...prev, periods: updatedPeriods }));
  };

  const updatePeriod = (index, field, value) => {
    const updatedPeriods = [...formData.periods];
    updatedPeriods[index] = {
      ...updatedPeriods[index],
      [field]: field === 'period_number' || field.includes('month') || field.includes('day') || field === 'year_shift' 
        ? parseInt(value) 
        : value
    };
    setFormData(prev => ({ ...prev, periods: updatedPeriods }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simple validation
    const totalPeriods = formData.number_of_posting_periods + formData.number_of_special_periods;
    if (formData.periods.length !== totalPeriods) {
      toast.error(`Number of periods (${formData.periods.length}) must equal posting + special periods (${totalPeriods})`);
      return;
    }
    
    if (totalPeriods > 16) {
      toast.error('Total periods cannot exceed 16');
      return;
    }
    
    onSubmit(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      {!editingItem && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <Label className="text-sm font-semibold mb-2 block">Quick Templates</Label>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => applyTemplate('K4')}>
              <Copy className="w-3 h-3 mr-1" /> K4 (Jan-Dec)
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => applyTemplate('V3')}>
              <Copy className="w-3 h-3 mr-1" /> V3 (Apr-Mar)
            </Button>
          </div>
        </div>
      )}

      {/* Basic Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fiscal Year Variant ID *</Label>
          <Input
            value={formData.fiscal_year_variant_id}
            onChange={(e) => updateField('fiscal_year_variant_id', e.target.value.toUpperCase())}
            maxLength={2}
            required
            disabled={!!editingItem}
            placeholder="e.g., K4, V3"
          />
          <p className="text-xs text-gray-500 mt-1">1-2 characters, uppercase</p>
        </div>
        
        <div>
          <Label>Description *</Label>
          <Input
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            required
            placeholder="e.g., Calendar Year Jan-Dec"
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div>
          <Label>Posting Periods *</Label>
          <Input
            type="number"
            min="1"
            max="16"
            value={formData.number_of_posting_periods}
            onChange={(e) => updateField('number_of_posting_periods', parseInt(e.target.value))}
            required
          />
        </div>
        
        <div>
          <Label>Special Periods *</Label>
          <Input
            type="number"
            min="0"
            max="4"
            value={formData.number_of_special_periods}
            onChange={(e) => updateField('number_of_special_periods', parseInt(e.target.value))}
            required
          />
        </div>

        <div className="flex items-center space-x-2 pt-6">
          <Checkbox
            id="calendar_year"
            checked={formData.calendar_year_flag}
            onCheckedChange={(checked) => updateField('calendar_year_flag', checked)}
          />
          <Label htmlFor="calendar_year" className="text-sm cursor-pointer">
            Calendar Year
          </Label>
        </div>

        <div className="flex items-center space-x-2 pt-6">
          <Checkbox
            id="year_dependent"
            checked={formData.year_dependent_flag}
            onCheckedChange={(checked) => updateField('year_dependent_flag', checked)}
          />
          <Label htmlFor="year_dependent" className="text-sm cursor-pointer">
            Year Dependent
          </Label>
        </div>
      </div>

      {/* Periods Management - Full functionality restored */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-lg font-semibold">
            Periods ({formData.periods.length} / {formData.number_of_posting_periods + formData.number_of_special_periods})
          </Label>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={generatePeriods}>
              <Copy className="w-3 h-3 mr-1" /> Auto-Generate
            </Button>
            <Button type="button" size="sm" onClick={addPeriod}>
              <Plus className="w-3 h-3 mr-1" /> Add Period
            </Button>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Start Month</th>
                <th className="px-2 py-2 text-left">Start Day</th>
                <th className="px-2 py-2 text-left">End Month</th>
                <th className="px-2 py-2 text-left">End Day</th>
                <th className="px-2 py-2 text-left">Year Shift</th>
                <th className="px-2 py-2 text-left">Type</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {formData.periods.map((period, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="px-2 py-2 font-mono">{period.period_number}</td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={period.month_start}
                      onChange={(e) => updatePeriod(index, 'month_start', e.target.value)}
                      className="h-8 w-16"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={period.day_start}
                      onChange={(e) => updatePeriod(index, 'day_start', e.target.value)}
                      className="h-8 w-16"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={period.month_end}
                      onChange={(e) => updatePeriod(index, 'month_end', e.target.value)}
                      className="h-8 w-16"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={period.day_end}
                      onChange={(e) => updatePeriod(index, 'day_end', e.target.value)}
                      className="h-8 w-16"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min="-1"
                      max="1"
                      value={period.year_shift}
                      onChange={(e) => updatePeriod(index, 'year_shift', e.target.value)}
                      className="h-8 w-16"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      period.period_number > formData.number_of_posting_periods
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {period.period_number > formData.number_of_posting_periods ? 'Special' : 'Posting'}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removePeriod(index)}
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="button" onClick={handleSubmit} className="flex-1">
          {editingItem ? 'Update' : 'Create'} Fiscal Year Variant
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default FiscalYearVariantForm;
